/**
 * 对金额类型的数字，按位数分割，并添加分割符
 * @param {Object}
 * separator 分隔符
 * length 分割间隙长度
 * pointStr 小数点符号
 */
export function getPriceFormatter({ separator = " ", length = 3, pointStr, precision = 0 }) {
  /**
   * @param {number} value
   */
  return (value) => {
    if (typeof value !== "number") {
      throw new Error("price must be a number: " + value);
    }
    if (precision) {
      value = value.toFixed(precision);
    }
    // 小数点处理
    let point;
    if (pointStr) {
      point = String(value).split(".")[1];
      point = point ? pointStr + point : point;
    }
    value = Math.floor(Number(value));
    return (
      (typeof value === "number" || typeof value === "string"
        ? String(value)
            .split("")
            .reverse()
            .reduce(
              (result, letter, index) => (
                result.unshift(letter, index > 0 && index % length === 0 ? separator : undefined),
                result
              ),
              []
            )
            .join("")
        : value) + (point || "")
    );
  };
}

// `.` 做分隔符，用 `,` 做小数点符 印尼和越南用
export const formatSeparatorDotAndPointComma = getPriceFormatter({ separator: ".", pointStr: "," });
// `,` 做分隔符，用 `.` 做小数点符 菲律宾和马来用
export const formatSeparatorCommaAndPointDot = getPriceFormatter({ separator: ",", pointStr: "." });

/**
 * 印尼价格简写
 * @param {number} value
 */
export function priceShortID(value) {
  if (typeof value !== "number") {
    throw new Error("price must be a number: " + value);
  }
  let price;

  if (value >= 1000 && value < 1000000) {
    price = `${Math.ceil(value / 1000)}rb`;
  } else if (value >= 1000000 && value < 1000000000) {
    price = `${`${Math.round(value / 10000) / 100}`.replace(".", ",")}jt`;
  } else if (value >= 1000000000) {
    price = `${`${Math.round(value / 10000000) / 100}`.replace(".", ",")}M`;
  } else {
    price = `${Math.ceil(value)}`.replace(".", ",");
  }

  return price;
}

/**
 * 越南价格简写
 * @param {number} value
 */
export function priceShortVN(value) {
  if (typeof value !== "number") {
    throw new Error("price must be a number: " + value);
  }
  return value >= 1000
    ? `${formatSeparatorDotAndPointComma(Number((Math.floor(value) / 1000).toFixed(2)))}K`
    : `${Math.ceil(value / 100) * 100}`.replace(".", ",");
}

/**
 * 菲律宾价格简写
 * @param {number} value
 */
export function priceShortPH(value) {
  if (typeof value !== "number") {
    throw new Error("price must be a number: " + value);
  }
  let price;

  if (value >= 1000 && value < 1000000) {
    price = `${(value / 1000).toFixed(2)}K`;
  } else if (value >= 1000000 && value < 1000000000) {
    price = `${(value / 1000000).toFixed(2)}M`;
  } else if (value >= 1000000000) {
    price = `${(value / 1000000000).toFixed(2)}B`;
  } else {
    price = `${Math.ceil(value)}`;
  }

  if (/\.00(K|M|B)$/.test(price)) {
    // 校验小数点后是否是两个0，如果是就去掉小数位
    return price.replace(".00", "");
  } else if (/\.[1-9]0(K|M|B)$/.test(price)) {
    // 校验小数位最后一位是否为0，如果是，就去掉最后一位的0
    return `${price.slice(0, -2)}${price.slice(-1)}`;
  } else {
    return price;
  }
}

/**
 * 马来西亚价格简写
 * @param {number} value
 */
export function priceShortRM(value) {
  if (typeof value !== "number") {
    throw new Error("price must be a number: " + value);
  }
  let price;

  if (value >= 1000000) {
    price = `${(value / 1000000).toFixed(2)} Juta`;
  } else {
    price = formatSeparatorCommaAndPointDot(Math.ceil(value * 100) / 100);
  }

  if (/\.00\sJuta$/.test(price)) {
    // 校验小数点后是否是两个0，如果是就去掉小数位
    return price.replace(".00", "");
  } else if (/\.[1-9]0\sJuta$/.test(price)) {
    // 校验小数位最后一位是否为0，如果是，就去掉最后一位的0
    return `${price.slice(0, -6)} Juta`;
  } else {
    return price;
  }
}

/**
 * 欧元价格简写
 * @param {number} value
 */
export const formatEuroPrice = getPriceFormatter({ separator: ".", pointStr: ",", precision: 2 });
